import { autoUpdate, Middleware, offset, useFloating } from '@floating-ui/react'
import { PropsWithChildren } from 'react'
import { createPortal } from 'react-dom'
import { TooltipProps } from 'recharts'
import {
    NameType,
    ValueType,
} from 'recharts/types/component/DefaultTooltipContent'

type TooltipPositionWrapperProps = PropsWithChildren & {
    tooltipProps: TooltipProps<ValueType, NameType>
}

/**
 * Wrapper component that adjusts tooltip position to prevent downward overflow.
 * If the tooltip overflows the chart bottom, it repositions upward so the tooltip
 * bottom aligns with the chart bottom.
 * Uses useLayoutEffect to calculate position synchronously before paint, preventing visual jump.
 */
export const TooltipPositionWrapper: React.FC<TooltipPositionWrapperProps> = (
    props
) => {
    const { children, tooltipProps } = props
    const coordinate = tooltipProps.coordinate

    const boundariesMiddleware: Middleware = {
        name: 'boundariesMiddleware',
        fn(props) {
            const x = props.x
            const y = props.y

            if (!tooltipProps.coordinate || !tooltipProps.viewBox) {
                return { x, y }
            }

            const tooltipRoot = props.elements.floating.getBoundingClientRect()
            const viewBox = tooltipProps.viewBox
            const coordinate = tooltipProps.coordinate

            // Get tooltip dimensions
            const tooltipWidth = tooltipRoot.width
            const tooltipHeight = tooltipRoot.height
            const tooltipX = coordinate.x ?? 0
            const tooltipY = coordinate.y ?? 0

            // Calculate chart boundaries
            const chartLeft = viewBox.x ?? 0
            const chartRight = (viewBox.x ?? 0) + (viewBox.width ?? 0)
            const chartBottom = (viewBox.y ?? 0) + (viewBox.height ?? 0)

            // Small offset to avoid overlapping the dot
            let xAdjustment = 10

            // Check if there's enough space on the right
            if (tooltipX + xAdjustment + tooltipWidth > chartRight) {
                // Not enough space on right, flip to left side of dot
                xAdjustment = -(tooltipWidth + xAdjustment)

                // Check if there's enough space on the left
                if (tooltipX + xAdjustment < chartLeft) {
                    // Not enough space on either side, align to chart edge
                    xAdjustment = -(tooltipX - chartLeft)
                }
            }

            // Check for downward overflow
            const tooltipBottom = tooltipY + tooltipHeight
            let yAdjustment = 0
            if (tooltipBottom > chartBottom) {
                // Move tooltip up so bottom aligns with chart bottom
                const overflow = tooltipBottom - chartBottom
                yAdjustment = -overflow
            }

            return { x: x + xAdjustment, y: y + yAdjustment }
        },
    }

    const { refs, floatingStyles } = useFloating({
        placement: 'bottom-start',
        whileElementsMounted: autoUpdate,
        middleware: [
            offset({
                mainAxis: coordinate?.y ?? 0,
                crossAxis: coordinate?.x ?? 0,
            }),
            boundariesMiddleware,
        ],
    })

    return (
        <div ref={refs.setReference}>
            {createPortal(
                <div
                    className="z-10 min-w-chartTooltipMinWidth max-w-chartTooltipMaxWidth"
                    ref={refs.setFloating}
                    style={floatingStyles}
                >
                    {children}
                </div>,
                document.body
            )}
        </div>
    )
}
